import { useSyncExternalStore } from "react";
import { request, RequestError } from "../lib/api";
import type { ContentItem, ModuleDetail } from "../types";
import { localizeItem } from "../lib/localize";
import {
  downloadMedia,
  hasMedia,
  offlineSupported,
  readCachedModule,
  requestPersistentStorage,
  writeCachedModule,
} from "./assetCache";
import { pendingCompletedItemIDs } from "./writeQueue";

// Per-item offline readiness, as the home screen's checklist shows it.
//   ready       — everything the item needs is on the device
//   downloading — one of its files is transferring now
//   queued      — waiting behind other files
//   failed      — a file could not be fetched; the item still works online
//   online_only — streams from a third party (YouTube, embeds, links) and
//                 can't be stored
export type ItemOfflineStatus = "ready" | "downloading" | "queued" | "failed" | "online_only";

export interface ModuleDownloadState {
  phase: "idle" | "loading" | "downloading" | "done" | "error";
  module: ModuleDetail | null;
  items: Record<string, ItemOfflineStatus>;
  /** 0–100 across all files in the module. */
  percent: number;
  error: string;
}

const idle: ModuleDownloadState = {
  phase: "idle",
  module: null,
  items: {},
  percent: 0,
  error: "",
};

const states = new Map<string, ModuleDownloadState>();
const running = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();

function set(moduleID: string, patch: Partial<ModuleDownloadState>): void {
  states.set(moduleID, { ...(states.get(moduleID) ?? idle), ...patch });
  listeners.forEach((listener) => listener());
}

const ONLINE_ONLY_TYPES = new Set(["external_link", "embed"]);

// Every file URL an item's renderer will load. Kept next to the renderers'
// payload shapes in components/content/types.ts — add a field there, add it
// here.
export function assetURLs(item: ContentItem): string[] {
  const c = item.content as Record<string, unknown>;
  const str = (value: unknown) => (typeof value === "string" && value ? [value] : []);
  const list = (value: unknown, key: string) =>
    Array.isArray(value)
      ? value.flatMap((entry) => str((entry as Record<string, unknown>)?.[key]))
      : [];
  switch (item.type) {
    case "video":
      return c.source === "uploaded" || !c.source
        ? [...str(c.video_url), ...str(c.poster_url), ...str(c.subtitle_url)]
        : [];
    case "audio":
      return [...str(c.audio_url), ...str(c.cover_image_url)];
    case "audio_slides":
      return [...str(c.audio_url), ...list(c.slides, "image_url")];
    case "image":
    case "gallery":
      return list(c.images, "url");
    case "slides":
    case "downloadable":
      return str(c.file_url);
    case "resource_pack":
    case "practical_activity":
      return list(c.files, "file_url");
    default:
      return [];
  }
}

// assetURLs plus the files of every translation (e.g. the Urdu video), so a
// teacher can switch language offline without losing media.
export function allAssetURLs(item: ContentItem): string[] {
  const urls = new Set(assetURLs(item));
  for (const language of Object.keys(item.translations ?? {})) {
    assetURLs(localizeItem(item, language)).forEach((url) => urls.add(url));
  }
  return [...urls];
}

function isOnlineOnly(item: ContentItem): boolean {
  if (ONLINE_ONLY_TYPES.has(item.type)) return true;
  const source = (item.content as { source?: string }).source;
  return item.type === "video" && Boolean(source) && source !== "uploaded";
}

// Queued completions haven't reached the server yet; see
// pendingCompletedItemIDs.
export async function withPendingProgress(module: ModuleDetail): Promise<ModuleDetail> {
  const pending = await pendingCompletedItemIDs();
  if (!pending.size) return module;
  return {
    ...module,
    items: module.items.map((item) =>
      pending.has(item.id) ? { ...item, progress: { status: "completed", percent: 100 } } : item
    ),
  };
}

// Network first — the server's copy carries fresh progress — falling back
// to the device copy when there is no connection. A server that answers
// (e.g. 423 once the PD Day has closed) is believed, not overridden.
export async function loadModule(moduleID: string): Promise<ModuleDetail> {
  try {
    const module = await request<ModuleDetail>(`/api/teacher/modules/${moduleID}`);
    void writeCachedModule(module);
    return withPendingProgress(module);
  } catch (cause) {
    if (cause instanceof RequestError) throw cause;
    const cached = await readCachedModule(moduleID);
    if (!cached) throw cause;
    return withPendingProgress(cached);
  }
}

async function run(moduleID: string): Promise<void> {
  set(moduleID, { phase: "loading", error: "" });
  void requestPersistentStorage();
  let module: ModuleDetail;
  try {
    module = await loadModule(moduleID);
  } catch (cause) {
    set(moduleID, {
      phase: "error",
      error: cause instanceof Error ? cause.message : "Download failed",
    });
    return;
  }

  const items: Record<string, ItemOfflineStatus> = {};
  const work: { itemID: string; url: string }[] = [];
  let totalFiles = 0;
  for (const item of module.items) {
    if (isOnlineOnly(item)) {
      items[item.id] = "online_only";
      continue;
    }
    const urls = offlineSupported() ? allAssetURLs(item) : [];
    totalFiles += urls.length;
    const missing: string[] = [];
    for (const url of urls) if (!(await hasMedia(url))) missing.push(url);
    items[item.id] = missing.length ? "queued" : "ready";
    missing.forEach((url) => work.push({ itemID: item.id, url }));
  }
  set(moduleID, {
    phase: work.length ? "downloading" : "done",
    module,
    items: { ...items },
    percent: work.length ? 0 : 100,
  });

  // Files download one at a time: a phone on a weak link does better
  // finishing one video than crawling through four in parallel.
  const failed = new Set<string>();
  let failedFiles = 0;
  for (let index = 0; index < work.length; index += 1) {
    const { itemID, url } = work[index];
    items[itemID] = "downloading";
    set(moduleID, { items: { ...items } });
    try {
      await downloadMedia(url, (loaded, total) => {
        const fraction = total ? Math.min(loaded / total, 1) : 0;
        set(moduleID, { percent: Math.round(((index + fraction) / work.length) * 100) });
      });
    } catch {
      failed.add(itemID);
      failedFiles += 1;
    }
    const itemDone = !work.slice(index + 1).some((entry) => entry.itemID === itemID);
    items[itemID] = failed.has(itemID) ? "failed" : itemDone ? "ready" : "queued";
    set(moduleID, {
      items: { ...items },
      percent: Math.round(((index + 1) / work.length) * 100),
    });
  }
  // After a failure, report what is actually on the phone rather than 100%.
  const savedPercent = Math.round(((totalFiles - failedFiles) / totalFiles) * 100);
  set(moduleID, {
    phase: failed.size ? "error" : "done",
    percent: failed.size ? savedPercent : 100,
  });
}

// Starts (or joins) the download of one module. Safe to call on every
// render of the home screen: a module already downloading isn't restarted,
// and a finished one only re-checks for files it's missing.
export function ensureModuleDownloaded(moduleID: string): Promise<void> {
  const existing = running.get(moduleID);
  if (existing) return existing;
  const job = run(moduleID).finally(() => running.delete(moduleID));
  running.set(moduleID, job);
  return job;
}

export function downloadState(moduleID: string): ModuleDownloadState {
  return states.get(moduleID) ?? idle;
}

export function subscribeDownloads(listener: () => void): () => void {
  return subscribe(listener);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useModuleDownload(moduleID: string | undefined): ModuleDownloadState {
  return useSyncExternalStore(subscribe, () => (moduleID ? (states.get(moduleID) ?? idle) : idle));
}
